import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Tag } from "lucide-react";
import { api, fmt } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

export default function ReturnLabelSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [rma, setRma] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/returns/status/${sessionId}`);
        setRma(data.rma);
        setPaymentStatus(data.payment_status);
        if (data.payment_status === "paid") return;
      } catch {}
      if (tries.current++ < 15) setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="label-success-page">
      {!rma || paymentStatus !== "paid" ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={36} className="animate-spin text-amber-400" />
          <p className="font-heading text-xl text-white">Confirming label payment…</p>
        </div>
      ) : (
        <>
          <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
          <h1 className="mt-5 font-heading text-3xl font-bold text-white" data-testid="label-issued-heading">Return label issued</h1>
          <p className="mt-2 text-slate-400">
            Return <span className="font-mono font-semibold text-amber-400" data-testid="label-rma-id">{rma.id}</span> for order{" "}
            <span className="font-mono text-slate-200">{rma.order_number}</span>
          </p>
          <div className="card-surface mt-8 p-6 text-left">
            <div className="flex items-center gap-3">
              <Tag size={18} className="text-amber-400" />
              <p className="font-heading font-semibold text-white">Your prepaid return label</p>
            </div>
            <div className="mt-4 space-y-2 text-sm text-slate-300">
              <p>Carrier: <span className="text-white">{rma.label?.carrier}</span></p>
              <p>Return tracking: <span className="font-mono text-amber-300" data-testid="label-tracking">{rma.label?.tracking_number}</span></p>
              <p>Label fee paid: {fmt(rma.label?.cost)}</p>
              <p>Expected refund after inspection: <span className="font-semibold text-emerald-400">{fmt(rma.refund_amount)}</span></p>
            </div>
            <div className="mt-4 flex items-center gap-2 border-t border-white/10 pt-4">
              <span className="text-xs text-slate-500">Status:</span>
              <StatusBadge status={rma.status} testid="label-rma-status" />
            </div>
            <p className="mt-4 text-xs text-slate-500">
              We emailed the label and next steps to you. Pack the item unused in its original packaging — once it
              arrives and passes inspection, your refund goes straight back to your original payment method.
            </p>
          </div>
          <Link to="/shop" className="btn-ghost mt-6" data-testid="label-continue-btn">Continue shopping</Link>
        </>
      )}
    </div>
  );
}
